// Purchase tab content for the Coach Dashboard.
// Renders a collapsible (icon-only by default) sub-navigation for the
// Purchase section plus the "My Purchase Requests" / deliveries views,
// matching the redesigned Purchase page mockup.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Menu,
    ClipboardList,
    Truck,
    PackageCheck,
    Boxes,
    Package,
    Users,
    Warehouse,
    FileSpreadsheet,
    BarChart3,
    FileText,
    Plus,
    Search,
    Eye,
    Pencil,
    Trash2,
    ChevronLeft,
    ChevronRight,
    ShoppingBag,
    BadgeCheck,
    LayoutList,
} from 'lucide-react';
import AddPurchaseRequestModal from './AddPurchaseRequestModal';
import DeletePurchaseRequestModal from './DeletePurchaseRequestModal';
import UpdatePurchaseStatusModal from './UpdatePurchaseStatusModal';
import NewProductApprovalModal from './NewProductApprovalModal';
import AdminPurchaseApprovalsView from './AdminPurchaseApprovalsView';
import VendorsView from './VendorsView';
import ProductsView from './ProductsView';
import PurchaseRequestAttachments from './PurchaseRequestAttachments';
import PurchaseSummaryCards, { CARD_STATUS, CARD_TITLE } from './PurchaseSummaryCards';
import UpdatePurchaseRequestDrawer from './UpdatePurchaseRequestDrawer';
import { isWaitingForViewer } from './productApprovalReview';
import {
    DELIVERING_FROM_STATUSES,
    DELIVERING_STATUS,
    awaitingDeliveryOf,
    deliveringLabel,
    requesterWithRole,
} from './purchaseDelivery';
import { countUnseen, markPurchaseItemsSeen, usePurchaseSeen } from './purchaseSeen';
import { getProductApprovals } from '../../../api';
import './PurchasePanel.css';
import SearchableSelect from '../../common/SearchableSelect';

const CATEGORY_OPTIONS = ['ISF Shop', 'Medicines', 'Consumables', 'Repairs', 'Infra', 'Others'];

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'pending_fulfillment', label: 'Pending Fulfillment' },
    { value: 'approved', label: 'Approved' },
    { value: 'ordered', label: 'Ordered' },
    { value: 'delivered_store', label: 'Delivered to Store' },
    { value: 'delivered_balagruha', label: 'Delivered to Balagruha' },
    { value: 'fulfilled', label: 'Fulfilled' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_TONE = {
    pending: 'yellow',
    pending_approval: 'yellow',
    pending_fulfillment: 'yellow',
    approved: 'green',
    ordered: 'blue',
    delivered_store: 'blue',
    delivered_balagruha: 'green',
    fulfilled: 'green',
    completed: 'green',
    on_hold: 'orange',
    rejected: 'red',
    cancelled: 'grey',
};

const CATEGORY_TONE = {
    'ISF Shop': 'pink',
    Consumables: 'blue',
    Infra: 'teal',
    Medicines: 'purple',
    Repairs: 'orange',
    Others: 'grey',
};

const PRIORITY_COLOR = { high: '#dc2626', medium: '#b45309', low: '#2563eb' };

const ITEMS_PER_PAGE = 10;

const formatDate = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString('en-IN');
};

const formatStatusLabel = (status) => {
    const found = STATUS_OPTIONS.find((option) => option.value === status);
    if (found) return found.label;
    if (!status) return 'N/A';
    return status.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

// Delivering (the store step) names the requester it is on its way to.
const statusLabelOf = (item) =>
    item.status === DELIVERING_STATUS && requesterWithRole(item)
        ? deliveringLabel(item)
        : formatStatusLabel(item.status);

const balagruhaIdOf = (item) => String(item.balagruhaId?._id || item.balagruhaId || '');
const requesterIdOf = (item) => String(item.requestedBy?._id || item.requestedBy || '');

const monthOf = (date) => {
    const parsed = date ? new Date(date) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) return '';
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
};

const getRequestItemsLabel = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return 'N/A';
    const names = items.map((entry) => entry.productName || entry.name || entry.shopItemId?.name).filter(Boolean);
    if (names.length === 0) return 'N/A';
    return names.length > 1 ? `${names[0]} +${names.length - 1} more` : names[0];
};

const getRequestQuantity = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return 'N/A';
    const total = items.reduce((sum, entry) => sum + Number(entry.requestedQuantity || entry.quantity || 0), 0);
    return total || 'N/A';
};

// The backend only lets the requester (or an admin) edit or delete a request.
const isOwnRequestOrAdmin = (item) => {
    if ((localStorage.getItem('role') || '').toLowerCase() === 'admin') return true;
    const userId = localStorage.getItem('userId');
    return !!userId && String(item.requestedBy?._id || item.requestedBy) === userId;
};

// Editing: only while 'pending'. Repairs requests belong to Repair
// Management, so not here.
const canEditRequest = (item) =>
    item.status === 'pending' && item.category !== 'Repairs' && isOwnRequestOrAdmin(item);

// Waiting at the store for delivery to the Balagruha: Edit only offers the
// status (-> 'delivered_balagruha'), which the backend lets the requester or
// an admin set.
const canUpdateDeliveryStatus = (item) => item.status === 'delivered_store' && isOwnRequestOrAdmin(item);

// Deleting: same statuses the backend allows a hard delete for.
const DELETABLE_STATUSES = ['pending', 'pending_approval', 'cancelled'];
const canDeleteRequest = (item) => DELETABLE_STATUSES.includes(item.status) && isOwnRequestOrAdmin(item);

// Deliveries pages (purchase requests; coaches / medical incharges see only
// their own): a request is
// a pending delivery from the moment it is made until it reaches the
// Balagruha / is completed. Rejected and cancelled ones are never delivered.
const COMPLETED_DELIVERY_STATUSES = ['delivered_balagruha', 'completed', 'fulfilled'];
const CLOSED_REQUEST_STATUSES = ['rejected', 'cancelled'];

const getPageButtons = (current, pageCount) => {
    const buttons = [];
    const start = Math.max(1, current - 2);
    const end = Math.min(pageCount, start + 4);
    for (let i = start; i <= end; i += 1) buttons.push(i);
    return buttons;
};

const SUB_NAV_REQUEST_ITEMS = [
    { key: 'requests', label: 'My Purchase Requests', icon: ClipboardList },
    { key: 'pendingDeliveries', label: 'Pending Deliveries', icon: Truck },
    { key: 'completedDeliveries', label: 'Completed Deliveries', icon: PackageCheck },
    { key: 'totalDeliveries', label: 'Total Deliveries', icon: Boxes },
];

const APPROVALS_NAV_ITEM = { key: 'productApprovals', label: 'Purchase Approvals', icon: BadgeCheck };

// Purchase manager's Pending Deliveries page: its filters (card = the summary
// card picked, which narrows the list to that card's status).
const NO_DELIVERY_FILTERS = { card: null, balagruha: 'all', category: 'all', requester: 'all', month: '', search: '' };

const SUB_NAV_MANAGE_ITEMS = [
    // No path: Products and Vendors open inside the panel (ProductsView, VendorsView).
    { key: 'products', label: 'Products', icon: Package },
    { key: 'vendors', label: 'Vendors', icon: Users },
    { key: 'inventory', label: 'Inventory', icon: Warehouse, path: '/shop/admin/inventory' },
    { key: 'masterReport', label: 'Master Report', icon: FileSpreadsheet, path: '/shop/admin/inventory/master-report' },
    { key: 'analytics', label: 'Analytics', icon: BarChart3, path: '/shop/admin/analytics' },
    { key: 'reports', label: 'Reports', icon: FileText, path: '/shop/admin/reports' },
];

export default function PurchasePanel({
    balagruhas = [],
    purchaseData = [],
    isfShopData = [],
    canCreate = false,
    canManageShop = false,
    onRequestCreated = () => {},
    // New Product Approval: 'requester' (coach / medical incharge - only the
    // New Product Approval button, no approvals list; opens on My Purchase
    // Requests, first in its sub-nav), 'pm' (purchase
    // manager) or 'admin' (admin Purchase page). 'admin' opens on the Purchase
    // Approvals page; 'pm' opens on its own Pending Deliveries page (cards,
    // filters, "Delivering to" status), with Purchase Approvals one click away.
    // Their request list is only reached from the Purchase Approvals cards and
    // "View All Purchases". Left unset, the panel is exactly as before.
    productApprovalMode = null,
}) {
    const navigate = useNavigate();
    const canSuggestProduct = productApprovalMode === 'requester';
    const canReviewProducts = productApprovalMode === 'pm';
    const showsAdminApprovals = productApprovalMode === 'admin';
    // The purchase manager and admin have the Purchase Approvals page.
    const approvalsHome = canReviewProducts || showsAdminApprovals;
    // Pending Deliveries is the purchase manager's first page; the admin opens
    // on Purchase Approvals, coaches and medical incharges on My Purchase Requests.
    const deliveriesHome = canReviewProducts;
    const [collapsed, setCollapsed] = useState(true);
    const [activeView, setActiveView] = useState(
        deliveriesHome ? 'pendingDeliveries' : approvalsHome ? 'productApprovals' : 'requests'
    );
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [statusEditingRequest, setStatusEditingRequest] = useState(null);
    const [deliveringRequest, setDeliveringRequest] = useState(null);
    const [deletingRequest, setDeletingRequest] = useState(null);
    const [expandedRequestId, setExpandedRequestId] = useState(null);
    const [showProductApprovalModal, setShowProductApprovalModal] = useState(false);
    const isApprovalsView = approvalsHome && activeView === 'productApprovals';
    const isPmDeliveriesView = canReviewProducts && activeView === 'pendingDeliveries';
    const isVendorsView = canManageShop && activeView === 'vendors';
    const isProductsView = canManageShop && activeView === 'products';
    // Products and Vendors share the vendor page look (light-blue page, own header).
    const isShopManageView = isVendorsView || isProductsView;
    // New Product Approval / Add Purchase Request: on the request list; for
    // coaches / medical incharges Add Purchase Request is on Pending Deliveries too.
    const showsRequestButtons = activeView === 'requests' || (canSuggestProduct && activeView === 'pendingDeliveries');
    const showsProductApprovalButton = canSuggestProduct && activeView === 'requests';
    // Sub-nav, landing page first. The purchase manager / admin have no request
    // list entry; Purchase Approvals takes its place.
    const navItemsFor = (...keys) => keys.map((key) => SUB_NAV_REQUEST_ITEMS.find((item) => item.key === key));
    const subNavItems = canReviewProducts
        ? [...navItemsFor('pendingDeliveries'), APPROVALS_NAV_ITEM, ...navItemsFor('completedDeliveries', 'totalDeliveries')]
        : showsAdminApprovals
        ? [APPROVALS_NAV_ITEM, ...navItemsFor('pendingDeliveries', 'completedDeliveries', 'totalDeliveries')]
        : SUB_NAV_REQUEST_ITEMS;

    // Approvals waiting for this viewer (Pending Approval card; the red count
    // is the new ones among them). The Purchase Approvals page reports every
    // (re)load; the purchase manager opens elsewhere, so it is loaded here first.
    const [approvals, setApprovals] = useState([]);
    const approvalsViewer = canReviewProducts ? 'pm' : 'admin';
    const waitingApprovals = useMemo(
        () => (approvalsHome ? approvals.filter((approval) => isWaitingForViewer(approval, approvalsViewer)) : []),
        [approvalsHome, approvals, approvalsViewer]
    );
    const approvalsWaiting = waitingApprovals.length;
    useEffect(() => {
        if (!canReviewProducts) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const response = await getProductApprovals();
                if (!cancelled && response?.success) setApprovals(response.data || []);
            } catch (error) {
                // No count; the Purchase Approvals page loads them again.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canReviewProducts]);

    // Purchase manager: Edit on a pending / ordered request opens the Update
    // Purchase Request drawer ("Delivering to <requester>"). Their own pending
    // requests keep the full edit form, which offers the same status. Repairs
    // requests belong to Repair Management, so not here.
    const canSendDelivering = (item) =>
        canReviewProducts &&
        DELIVERING_FROM_STATUSES.includes(item.status) &&
        item.category !== 'Repairs' &&
        !canEditRequest(item);

    const [balagruhaFilter, setBalagruhaFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [monthFilter, setMonthFilter] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [deliveriesPage, setDeliveriesPage] = useState(1);
    const [deliveryFilters, setDeliveryFilters] = useState(NO_DELIVERY_FILTERS);
    // Where the request list's back link goes (the page that opened it)
    const [requestsBackView, setRequestsBackView] = useState('productApprovals');

    const setDeliveryFilter = (key, value) => {
        setDeliveryFilters((prev) => ({ ...prev, [key]: value }));
        setDeliveriesPage(1);
    };

    const resetPage = () => setPage(1);

    const pendingDeliveries = useMemo(
        () => isfShopData.filter((order) => order.deliveryStatus === 'pending_delivery'),
        [isfShopData]
    );
    const completedDeliveries = useMemo(
        () => isfShopData.filter((order) => order.deliveryStatus === 'delivered'),
        [isfShopData]
    );

    // The purchase manager's and admin's deliveries pages list the purchase
    // requests (theirs and everyone else's they can see): a new request shows
    // under Pending Deliveries straight away and moves to Completed
    // Deliveries once it is delivered / completed. Coaches and medical
    // incharges see only their own requests there (the panel is only given
    // the ones they made); their ISF Shop orders stay on Completed / Total
    // Deliveries, under the requests.
    const requestDeliveries = approvalsHome;
    const ownRequestDeliveries = canSuggestProduct;
    const requestDeliveryLists = useMemo(() => {
        const deliveries = purchaseData.filter((item) => !CLOSED_REQUEST_STATUSES.includes(item.status));
        return {
            pendingDeliveries: deliveries.filter((item) => !COMPLETED_DELIVERY_STATUSES.includes(item.status)),
            completedDeliveries: deliveries.filter((item) => COMPLETED_DELIVERY_STATUSES.includes(item.status)),
            totalDeliveries: deliveries,
        };
    }, [purchaseData]);

    // Purchase manager's Pending Deliveries page, with its filters applied.
    const pmPendingDeliveries = useMemo(() => {
        const { card, balagruha, category, requester, month, search: text } = deliveryFilters;
        const query = text.trim().toLowerCase();
        return requestDeliveryLists.pendingDeliveries.filter((item) => {
            if (card && item.status !== CARD_STATUS[card]) return false;
            if (balagruha !== 'all' && balagruhaIdOf(item) !== balagruha) return false;
            if (category !== 'all' && item.category !== category) return false;
            if (requester !== 'all' && requesterIdOf(item) !== requester) return false;
            if (month && monthOf(item.createdAt) !== month) return false;
            if (query) {
                const haystack = `${item.requestId || ''} ${getRequestItemsLabel(item.items)} ${item.requestedBy?.name || item.requestedByName || ''} ${statusLabelOf(item)}`.toLowerCase();
                if (!haystack.includes(query)) return false;
            }
            return true;
        });
    }, [requestDeliveryLists, deliveryFilters]);

    const deliveryRequesterOptions = useMemo(() => {
        const labels = new Map();
        requestDeliveryLists.pendingDeliveries.forEach((item) => {
            const id = requesterIdOf(item);
            const label = requesterWithRole(item);
            if (id && label) labels.set(id, label);
        });
        return [...labels.entries()]
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [requestDeliveryLists]);

    const filteredRequests = useMemo(() => {
        return purchaseData.filter((item) => {
            const itemBalagruhaId = item.balagruhaId?._id || item.balagruhaId;
            if (balagruhaFilter !== 'all' && itemBalagruhaId !== balagruhaFilter) return false;
            if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
            if (statusFilter !== 'all' && item.status !== statusFilter) return false;

            if (monthFilter) {
                const created = item.createdAt ? new Date(item.createdAt) : null;
                if (!created || Number.isNaN(created.getTime())) return false;
                const createdMonth = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`;
                if (createdMonth !== monthFilter) return false;
            }

            if (search.trim()) {
                const haystack = `${item.requestId || ''} ${getRequestItemsLabel(item.items)} ${item.requestedBy?.name || item.requestedByName || ''} ${item.status || ''}`.toLowerCase();
                if (!haystack.includes(search.trim().toLowerCase())) return false;
            }

            return true;
        });
    }, [purchaseData, balagruhaFilter, categoryFilter, statusFilter, monthFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filteredRequests.length / ITEMS_PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const paginatedRequests = filteredRequests.slice(
        (safePage - 1) * ITEMS_PER_PAGE,
        safePage * ITEMS_PER_PAGE
    );

    const handleSelectView = (key) => {
        setActiveView(key);
        setExpandedRequestId(null);
        setDeliveriesPage(1);
    };

    // Purchase Approvals: "View All Purchases" and the summary cards open the
    // request list, filtered to that status ('all' = every request). The
    // purchase manager's Pending Deliveries has "View All Purchases" too; the
    // list's back link returns to the page it was opened from.
    const showRequests = (status = 'all', fromView = 'productApprovals') => {
        setBalagruhaFilter('all');
        setCategoryFilter('all');
        setMonthFilter('');
        setSearch('');
        setStatusFilter(status);
        resetPage();
        setRequestsBackView(fromView);
        handleSelectView('requests');
    };

    // Coach / medical incharge: their count is about requests waiting for them
    // to mark Delivered to Balagruha, not every request.
    const requestsPageItems = useMemo(
        () => (canSuggestProduct ? awaitingDeliveryOf(purchaseData) : purchaseData),
        [canSuggestProduct, purchaseData]
    );

    // Coach / medical incharge deliveries pages: their requests, and on
    // Completed / Total Deliveries the ISF Shop orders.
    const ownDeliveryLists = useMemo(
        () =>
            ownRequestDeliveries
                ? {
                      pendingDeliveries: requestDeliveryLists.pendingDeliveries,
                      completedDeliveries: [...requestDeliveryLists.completedDeliveries, ...completedDeliveries],
                      totalDeliveries: [...requestDeliveryLists.totalDeliveries, ...isfShopData],
                  }
                : null,
        [ownRequestDeliveries, requestDeliveryLists, completedDeliveries, isfShopData]
    );

    // What each sub-nav page's count is about.
    const pageItemsOf = (key) => {
        if (key === 'productApprovals') return waitingApprovals;
        if (key === 'requests') return requestsPageItems;
        if (requestDeliveries) return requestDeliveryLists[key];
        if (ownDeliveryLists?.[key]) return ownDeliveryLists[key];
        if (key === 'pendingDeliveries') return pendingDeliveries;
        if (key === 'completedDeliveries') return completedDeliveries;
        if (key === 'totalDeliveries') return isfShopData;
        return null;
    };

    // Red counts show only what is new since that page was last opened; the
    // open page is seen (also what arrives while it is open).
    const seenScope = productApprovalMode || 'default';
    const seen = usePurchaseSeen(seenScope);
    const navCount = (key) => (key === activeView ? 0 : countUnseen(seen[key], pageItemsOf(key) || []));
    const approvalsNew = approvalsHome ? navCount('productApprovals') : 0;
    const openPageItems = pageItemsOf(activeView);
    useEffect(() => {
        if (openPageItems) markPurchaseItemsSeen(seenScope, activeView, openPageItems);
    }, [seenScope, activeView, openPageItems]);

    // Purchase manager's Pending Deliveries: a summary card narrows the list to
    // its status (again to show everything); Pending Approval opens Purchase
    // Approvals.
    const handleDeliveryCard = (key) => {
        if (key === 'pendingApproval') {
            handleSelectView('productApprovals');
            return;
        }
        setDeliveryFilter('card', deliveryFilters.card === key ? null : key);
    };

    const renderDeliveriesTable = (orders) => (
        <div className="pp-table-wrapper">
            <table className="pp-table">
                <thead>
                    <tr>
                        <th>Sr. No.</th>
                        <th>Order #</th>
                        <th>Student</th>
                        <th>Balagruha</th>
                        <th>Placed</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.length === 0 ? (
                        <tr>
                            <td colSpan={6} className="pp-empty-cell">No deliveries found</td>
                        </tr>
                    ) : (
                        orders.map((order, index) => (
                            <tr key={order._id || order.orderNumber}>
                                <td>{index + 1}</td>
                                <td>{order.orderNumber || 'N/A'}</td>
                                <td>{order.userId?.name || 'Unknown Student'}</td>
                                <td>{order.balagruhaNames || 'N/A'}</td>
                                <td>{formatDate(order.placedAt || order.createdAt)}</td>
                                <td>
                                    <span className={`pp-badge pp-badge-${order.deliveryStatus === 'delivered' ? 'green' : 'blue'}`}>
                                        {order.deliveryStatus === 'delivered' ? 'Delivered' : 'Pending Delivery'}
                                    </span>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );

    // The purchase request table (request list + the purchase manager /
    // admin deliveries pages): one row per request with its actions.
    // `labelled` adds View / Edit text to the action buttons (Delete stays an icon).
    const renderRequestTable = (rows, firstIndex, emptyText, labelled = false) => (
        <div className="pp-table-wrapper">
            <table className="pp-table">
                <thead>
                    <tr>
                        <th>Sr. No.</th>
                        <th>Requested By</th>
                        <th>Date</th>
                        <th>Due Date</th>
                        <th>Item Name</th>
                        <th>Category</th>
                        <th>Quantity</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={9} className="pp-empty-cell">{emptyText}</td>
                        </tr>
                    ) : (
                        rows.map((item, index) => {
                            const rowKey = item._id || item.requestId || index;
                            const isExpanded = expandedRequestId === rowKey;
                            const priorityColor = PRIORITY_COLOR[item.priority] || '#334155';
                            const categoryTone = CATEGORY_TONE[item.category] || 'grey';
                            const statusTone = STATUS_TONE[item.status] || 'grey';
                            const actionClass = labelled ? 'pp-icon-btn pp-icon-btn-labelled' : 'pp-icon-btn';
                            const isDelivering = item.status === DELIVERING_STATUS && !!requesterWithRole(item);

                            return (
                                <React.Fragment key={rowKey}>
                                    <tr>
                                        <td>{firstIndex + index + 1}</td>
                                        <td>{item.requestedBy?.name || item.requestedByName || 'N/A'}</td>
                                        <td>{formatDate(item.createdAt)}</td>
                                        <td>{formatDate(item.deadline)}</td>
                                        <td style={{ color: priorityColor, fontWeight: 700 }}>
                                            {getRequestItemsLabel(item.items)}
                                        </td>
                                        <td>
                                            <span className={`pp-badge pp-badge-${categoryTone}`}>
                                                {item.category || 'N/A'}
                                            </span>
                                        </td>
                                        <td>{getRequestQuantity(item.items)}</td>
                                        <td>
                                            <span className={`pp-badge pp-badge-${statusTone}${isDelivering ? ' pp-badge-wrap' : ''}`}>
                                                {statusLabelOf(item)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="pp-row-actions">
                                                <button
                                                    type="button"
                                                    className={actionClass}
                                                    title="View details"
                                                    onClick={() => setExpandedRequestId(isExpanded ? null : rowKey)}
                                                >
                                                    <Eye size={15} />
                                                    {labelled && <span>View</span>}
                                                </button>
                                                {canEditRequest(item) && (
                                                    <button
                                                        type="button"
                                                        className={actionClass}
                                                        title="Edit request"
                                                        aria-label="Edit request"
                                                        onClick={() => setEditingRequest(item)}
                                                    >
                                                        <Pencil size={15} />
                                                        {labelled && <span>Edit</span>}
                                                    </button>
                                                )}
                                                {canUpdateDeliveryStatus(item) && (
                                                    <button
                                                        type="button"
                                                        className={actionClass}
                                                        title="Edit request"
                                                        aria-label="Edit request status"
                                                        onClick={() => setStatusEditingRequest(item)}
                                                    >
                                                        <Pencil size={15} />
                                                        {labelled && <span>Edit</span>}
                                                    </button>
                                                )}
                                                {canSendDelivering(item) && (
                                                    <button
                                                        type="button"
                                                        className={actionClass}
                                                        title={`Edit request: ${deliveringLabel(item)}`}
                                                        aria-label="Update delivery status"
                                                        onClick={() => setDeliveringRequest(item)}
                                                    >
                                                        <Pencil size={15} />
                                                        {labelled && <span>Edit</span>}
                                                    </button>
                                                )}
                                                {canDeleteRequest(item) && (
                                                    <button
                                                        type="button"
                                                        className={`${actionClass} pp-icon-btn-danger`}
                                                        title="Delete request"
                                                        aria-label="Delete request"
                                                        onClick={() => setDeletingRequest(item)}
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className="pp-details-row">
                                            <td colSpan={9}>
                                                <div className="pp-details-panel">
                                                    <div>
                                                        <strong>Request ID:</strong> {item.requestId || item._id || 'N/A'}
                                                    </div>
                                                    <div>
                                                        <strong>Items:</strong>
                                                        <ul>
                                                            {(item.items || []).map((entry, i) => (
                                                                <li key={i}>
                                                                    {entry.productName || entry.name} — Qty {entry.requestedQuantity || entry.quantity || 0}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    {item.justification && (
                                                        <div><strong>Notes:</strong> {item.justification}</div>
                                                    )}
                                                    {Array.isArray(item.attachments) && item.attachments.length > 0 && (
                                                        <div>
                                                            <strong>Attachments:</strong> {item.attachments.length} file(s)
                                                            <PurchaseRequestAttachments requestId={item._id} attachments={item.attachments} />
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );

    const renderPagination = (total, currentPage, pageCount, onPageChange, noun) => (
        <div className="pp-pagination">
            <span className="pp-pagination-info">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total} {noun}
            </span>
            <div className="pp-pagination-controls">
                <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                >
                    <ChevronLeft size={16} />
                </button>
                {getPageButtons(currentPage, pageCount).map((num) => (
                    <button
                        key={num}
                        type="button"
                        className={num === currentPage ? 'active' : ''}
                        onClick={() => onPageChange(num)}
                    >
                        {num}
                    </button>
                ))}
                <button
                    type="button"
                    disabled={currentPage === pageCount}
                    onClick={() => onPageChange(Math.min(pageCount, currentPage + 1))}
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );

    // Purchase manager / admin deliveries page: its requests, 10 a page.
    const renderRequestDeliveries = (rows, emptyText = 'No deliveries found', noun = 'deliveries') => {
        const pageCount = Math.max(1, Math.ceil(rows.length / ITEMS_PER_PAGE));
        const currentPage = Math.min(deliveriesPage, pageCount);
        const firstIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return (
            <>
                {renderRequestTable(rows.slice(firstIndex, firstIndex + ITEMS_PER_PAGE), firstIndex, emptyText)}
                {rows.length > 0 && renderPagination(rows.length, currentPage, pageCount, setDeliveriesPage, noun)}
            </>
        );
    };

    // Coach / medical incharge deliveries page: their own purchase requests,
    // then (Completed / Total Deliveries) the ISF Shop orders as before.
    const renderOwnDeliveries = (requests, orders = null) => (
        <>
            <section className="pp-delivery-section" aria-label="My Purchase Requests">
                <h4 className="pp-delivery-section-title">My Purchase Requests</h4>
                {renderRequestDeliveries(requests, 'No purchase requests found', 'purchase requests')}
            </section>
            {orders && (
                <section className="pp-delivery-section" aria-label="ISF Shop Orders">
                    <h4 className="pp-delivery-section-title">ISF Shop Orders</h4>
                    {renderDeliveriesTable(orders)}
                </section>
            )}
        </>
    );

    // Purchase manager's first page: Pending Deliveries with the summary cards,
    // filters and the request table (Edit = "Delivering to <requester>").
    const renderPmPendingDeliveries = () => {
        const rows = pmPendingDeliveries;
        const pageCount = Math.max(1, Math.ceil(rows.length / ITEMS_PER_PAGE));
        const currentPage = Math.min(deliveriesPage, pageCount);
        const firstIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const title = deliveryFilters.card ? CARD_TITLE[deliveryFilters.card] : 'Pending Deliveries';

        return (
            <div className="apa-root pmd-root">
                <div className="apa-header">
                    <div className="apa-heading">
                        <span className="apa-heading-icon"><ShoppingBag size={22} color="#ffffff" aria-hidden="true" /></span>
                        <div>
                            <h2>Purchase</h2>
                            <p>Buy · Track · Manage Inventory</p>
                        </div>
                    </div>
                    <div className="pp-header-actions">
                        <button type="button" className="apa-outline-btn" onClick={() => showRequests('all', 'pendingDeliveries')}>
                            <LayoutList size={16} aria-hidden="true" /> View All Purchases
                        </button>
                        <button
                            type="button"
                            className="pp-add-btn pp-npa-btn pp-approvals-btn"
                            onClick={() => handleSelectView('productApprovals')}
                            aria-label={approvalsNew > 0 ? `Purchase Approvals, ${approvalsNew} new` : 'Purchase Approvals'}
                        >
                            <BadgeCheck size={16} /> Purchase Approvals
                            {approvalsNew > 0 && (
                                <span className="pp-count-badge" aria-hidden="true">{approvalsNew}</span>
                            )}
                        </button>
                        {canCreate && (
                            <button type="button" className="pp-add-btn" onClick={() => setShowAddModal(true)}>
                                <Plus size={16} /> Add Purchase Request
                            </button>
                        )}
                    </div>
                </div>

                <PurchaseSummaryCards
                    purchaseData={purchaseData}
                    pendingApprovals={approvalsWaiting}
                    approvalsHint="Need your review"
                    activeKey={deliveryFilters.card}
                    onSelect={handleDeliveryCard}
                    // "Delivering to" moves a request past Ordered in one go,
                    // so Pending Delivery to Yashodhan is left out here.
                    hiddenKeys={['pendingStoreDelivery']}
                />

                <div className="apa-filters pmd-filters">
                    <label className="apa-field">
                        <span>Balagruha Name</span>
                        <SearchableSelect
                            searchPlaceholder="Search balagruha..."
                            value={deliveryFilters.balagruha}
                            onChange={(e) => setDeliveryFilter('balagruha', e.target.value)}
                        >
                            <option value="all">All Balagruhas</option>
                            {balagruhas.map((bg) => (
                                <option key={bg._id} value={bg._id}>{bg.name}</option>
                            ))}
                        </SearchableSelect>
                    </label>
                    <label className="apa-field">
                        <span>Category</span>
                        <select value={deliveryFilters.category} onChange={(e) => setDeliveryFilter('category', e.target.value)}>
                            <option value="all">All Categories</option>
                            {CATEGORY_OPTIONS.map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </label>
                    <label className="apa-field">
                        <span>Requested By</span>
                        <SearchableSelect
                            searchPlaceholder="Search requester..."
                            value={deliveryFilters.requester}
                            onChange={(e) => setDeliveryFilter('requester', e.target.value)}
                        >
                            <option value="all">All Requesters</option>
                            {deliveryRequesterOptions.map((requester) => (
                                <option key={requester.id} value={requester.id}>{requester.label}</option>
                            ))}
                        </SearchableSelect>
                    </label>
                    <label className="apa-field">
                        <span>Date Range</span>
                        <input
                            type="month"
                            value={deliveryFilters.month}
                            onChange={(e) => setDeliveryFilter('month', e.target.value)}
                        />
                    </label>
                </div>

                <div className="apa-search pmd-search">
                    <Search size={16} className="apa-search-icon" aria-hidden="true" />
                    <input
                        type="text"
                        aria-label="Search pending deliveries"
                        placeholder="Search by item name, requester..."
                        value={deliveryFilters.search}
                        onChange={(e) => setDeliveryFilter('search', e.target.value)}
                    />
                </div>

                <section className="apa-table-card pmd-table-card">
                    <div className="apa-table-head">
                        <h3>{title} <span>(Purchase Requests)</span></h3>
                        {deliveryFilters.card && (
                            <button type="button" className="apa-link-btn" onClick={() => setDeliveryFilter('card', null)}>
                                Show all pending deliveries
                            </button>
                        )}
                    </div>
                    {renderRequestTable(rows.slice(firstIndex, firstIndex + ITEMS_PER_PAGE), firstIndex, 'No deliveries found', true)}
                    {rows.length > 0 &&
                        renderPagination(rows.length, currentPage, pageCount, setDeliveriesPage, `purchase requests (${title})`)}
                </section>
            </div>
        );
    };

    return (
        <div className="pp-root">
            <aside className={`pp-subnav ${collapsed ? 'pp-subnav-collapsed' : ''}`}>
                <button
                    type="button"
                    className="pp-subnav-toggle"
                    onClick={() => setCollapsed((prev) => !prev)}
                    title={collapsed ? 'Expand menu' : 'Collapse menu'}
                    aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                >
                    <Menu size={18} />
                </button>

                <nav className="pp-subnav-list">
                    {subNavItems.map((item) => {
                        const Icon = item.icon;
                        const count = navCount(item.key);

                        return (
                            <button
                                key={item.key}
                                type="button"
                                className={`pp-subnav-item ${activeView === item.key ? 'active' : ''}`}
                                onClick={() => handleSelectView(item.key)}
                                title={item.label}
                            >
                                <span className="pp-subnav-icon"><Icon size={16} /></span>
                                <span className="pp-subnav-label">{item.label}</span>
                                {count > 0 && <span className="pp-subnav-badge">{count}</span>}
                            </button>
                        );
                    })}

                    {canManageShop && (
                        <>
                            <div className="pp-subnav-divider" />
                            {SUB_NAV_MANAGE_ITEMS.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className={`pp-subnav-item ${activeView === item.key ? 'active' : ''}`}
                                        onClick={() => (item.path ? navigate(item.path) : handleSelectView(item.key))}
                                        title={item.label}
                                    >
                                        <span className="pp-subnav-icon"><Icon size={16} /></span>
                                        <span className="pp-subnav-label">{item.label}</span>
                                    </button>
                                );
                            })}
                        </>
                    )}
                </nav>
            </aside>

            {/* Purchase Approvals, the purchase manager's Pending Deliveries, Products and Vendors have their own header and cards */}
            <div className={isApprovalsView || isPmDeliveriesView ? 'pp-main apa-host' : isShopManageView ? 'pp-main vv-host' : 'pp-main'}>
                {!isApprovalsView && !isPmDeliveriesView && !isShopManageView && (
                    <div className="pp-main-header">
                        <div className="pp-main-heading">
                            <div className="pp-main-icon"><ShoppingBag size={20} color="#ffffff" /></div>
                            <div>
                                <h2>Purchase</h2>
                                <p>Buy · Track · Manage Inventory</p>
                            </div>
                        </div>

                        <div className="pp-header-actions">
                            {approvalsHome && activeView === 'requests' && (
                                <button type="button" className="pp-link-btn" onClick={() => handleSelectView(requestsBackView)}>
                                    ← Back to {requestsBackView === 'pendingDeliveries' ? 'Pending Deliveries' : 'Purchase Approvals'}
                                </button>
                            )}
                            {showsProductApprovalButton && (
                                <button type="button" className="pp-add-btn pp-npa-btn" onClick={() => setShowProductApprovalModal(true)}>
                                    <Plus size={16} /> New Product Approval
                                </button>
                            )}
                            {showsRequestButtons && canCreate && (
                                <button type="button" className="pp-add-btn" onClick={() => setShowAddModal(true)}>
                                    <Plus size={16} /> Add Purchase Request
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {activeView === 'requests' && (
                    <>
                        <div className="pp-filters">
                            <div className="pp-filter-field">
                                <label>Balagruha Name</label>
                                <SearchableSelect
                                    searchPlaceholder="Search balagruha..."
                                    value={balagruhaFilter}
                                    onChange={(e) => { setBalagruhaFilter(e.target.value); resetPage(); }}
                                >
                                    <option value="all">All Balagruhas</option>
                                    {balagruhas.map((bg) => (
                                        <option key={bg._id} value={bg._id}>{bg.name}</option>
                                    ))}
                                </SearchableSelect>
                            </div>

                            <div className="pp-filter-field">
                                <label>Category</label>
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => { setCategoryFilter(e.target.value); resetPage(); }}
                                >
                                    <option value="all">All Categories</option>
                                    {CATEGORY_OPTIONS.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="pp-filter-field">
                                <label>Status</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); resetPage(); }}
                                >
                                    <option value="all">All Status</option>
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="pp-filter-field">
                                <label>Date Range</label>
                                <input
                                    type="month"
                                    value={monthFilter}
                                    onChange={(e) => { setMonthFilter(e.target.value); resetPage(); }}
                                />
                            </div>
                        </div>

                        <div className="pp-search-row">
                            <div className="pp-search-box">
                                <Search size={16} className="pp-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by item name, requester..."
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                                />
                            </div>
                        </div>

                        {renderRequestTable(paginatedRequests, (safePage - 1) * ITEMS_PER_PAGE, 'No purchase requests found')}

                        {filteredRequests.length > 0 &&
                            renderPagination(filteredRequests.length, safePage, totalPages, setPage, 'purchase requests')}
                    </>
                )}

                {isApprovalsView && (
                    <AdminPurchaseApprovalsView
                        viewer={canReviewProducts ? 'pm' : 'admin'}
                        balagruhas={balagruhas}
                        purchaseData={purchaseData}
                        onShowRequests={showRequests}
                        onApprovalsLoaded={setApprovals}
                    />
                )}

                {isPmDeliveriesView && renderPmPendingDeliveries()}

                {isVendorsView && <VendorsView />}

                {isProductsView && <ProductsView />}

                {activeView === 'pendingDeliveries' && !isPmDeliveriesView && (
                    <>
                        <div className="pp-subheader">
                            <h3>Pending Deliveries</h3>
                        </div>
                        {requestDeliveries
                            ? renderRequestDeliveries(requestDeliveryLists.pendingDeliveries)
                            : ownRequestDeliveries
                            ? renderOwnDeliveries(requestDeliveryLists.pendingDeliveries)
                            : renderDeliveriesTable(pendingDeliveries)}
                    </>
                )}

                {activeView === 'completedDeliveries' && (
                    <>
                        <div className="pp-subheader">
                            <h3>Completed Deliveries</h3>
                        </div>
                        {requestDeliveries
                            ? renderRequestDeliveries(requestDeliveryLists.completedDeliveries)
                            : ownRequestDeliveries
                            ? renderOwnDeliveries(requestDeliveryLists.completedDeliveries, completedDeliveries)
                            : renderDeliveriesTable(completedDeliveries)}
                    </>
                )}

                {activeView === 'totalDeliveries' && (
                    <>
                        <div className="pp-subheader">
                            <h3>Total Deliveries</h3>
                        </div>
                        {requestDeliveries
                            ? renderRequestDeliveries(requestDeliveryLists.totalDeliveries)
                            : ownRequestDeliveries
                            ? renderOwnDeliveries(requestDeliveryLists.totalDeliveries, isfShopData)
                            : renderDeliveriesTable(isfShopData)}
                    </>
                )}
            </div>

            {showProductApprovalModal && (
                <NewProductApprovalModal
                    mode="create"
                    balagruhas={balagruhas}
                    onClose={() => setShowProductApprovalModal(false)}
                    onDone={() => setShowProductApprovalModal(false)}
                />
            )}

            {showAddModal && (
                <AddPurchaseRequestModal
                    balagruhas={balagruhas}
                    onClose={() => setShowAddModal(false)}
                    onCreated={() => {
                        setShowAddModal(false);
                        onRequestCreated();
                    }}
                />
            )}

            {editingRequest && (
                <AddPurchaseRequestModal
                    balagruhas={balagruhas}
                    request={editingRequest}
                    canMarkDelivering={canReviewProducts}
                    onClose={() => setEditingRequest(null)}
                    onUpdated={() => {
                        setEditingRequest(null);
                        // Same list refresh the dashboards run after a create
                        onRequestCreated();
                    }}
                />
            )}

            {statusEditingRequest && (
                <UpdatePurchaseStatusModal
                    request={statusEditingRequest}
                    balagruhas={balagruhas}
                    onClose={() => setStatusEditingRequest(null)}
                    onUpdated={() => {
                        setStatusEditingRequest(null);
                        onRequestCreated();
                    }}
                />
            )}

            {deliveringRequest && (
                <UpdatePurchaseRequestDrawer
                    request={deliveringRequest}
                    balagruhas={balagruhas}
                    onClose={() => setDeliveringRequest(null)}
                    onUpdated={() => {
                        setDeliveringRequest(null);
                        onRequestCreated();
                    }}
                />
            )}

            {deletingRequest && (
                <DeletePurchaseRequestModal
                    request={deletingRequest}
                    itemsLabel={getRequestItemsLabel(deletingRequest.items)}
                    onClose={() => setDeletingRequest(null)}
                    onDeleted={() => {
                        if (expandedRequestId === (deletingRequest._id || deletingRequest.requestId)) {
                            setExpandedRequestId(null);
                        }
                        setDeletingRequest(null);
                        onRequestCreated();
                    }}
                />
            )}
        </div>
    );
}
